import { useState, type FormEvent } from "react";
import FormField, {
  inputClass,
  textareaClass,
  submitButtonClass,
} from "@/components/base/FormField";
import { submitReaddyForm, validateForm, HONEYPOT_FIELD } from "@/lib/submitForm";

const SUBMIT_ADDR = "https://readdy.ai/api/form/damjr8d1d38lmdrdaqlg";
const SUCCESS_MSG = "已收到講師邀約資訊，後續將確認活動時間、主題與合作方式。";
const FAIL_MSG = "目前訊息暫時無法送出，請稍後再試，或透過 LINE 與我們聯絡。";

export default function SpeakerForm() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const hp = (fd.get(HONEYPOT_FIELD) as string)?.trim() || "";
    if (hp) {
      setStatus("success");
      return;
    }
    const errs = validateForm(fd, ["name", "company", "email", "event_name", "topic"], "email");
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");
    const res = await submitReaddyForm(form, SUBMIT_ADDR);
    if (res.ok) {
      setStatus("success");
      form.reset();
    } else {
      setStatus("error");
      setErrorMsg(res.message || FAIL_MSG);
    }
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="speaker-invitation-form" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField label="姓名" name="name" required error={errors.name}>
          <input id="name" name="name" className={inputClass} placeholder="您的姓名" />
        </FormField>
        <FormField label="公司／單位" name="company" required error={errors.company}>
          <input id="company" name="company" className={inputClass} placeholder="公司或單位名稱" />
        </FormField>
        <FormField label="Email" name="email" required error={errors.email}>
          <input
            id="email"
            name="email"
            type="email"
            className={inputClass}
            placeholder="您的 Email"
          />
        </FormField>
        <FormField label="電話" name="phone">
          <input id="phone" name="phone" className={inputClass} placeholder="聯絡電話" />
        </FormField>
        <FormField label="活動名稱" name="event_name" required error={errors.event_name}>
          <input id="event_name" name="event_name" className={inputClass} placeholder="活動名稱" />
        </FormField>
        <FormField label="日期" name="date">
          <input id="date" name="date" type="date" className={inputClass} />
        </FormField>
        <FormField label="地點" name="location">
          <input id="location" name="location" className={inputClass} placeholder="活動地點" />
        </FormField>
        <FormField label="參與人數" name="headcount">
          <input
            id="headcount"
            name="headcount"
            className={inputClass}
            placeholder="預計參與人數"
          />
        </FormField>
        <FormField label="參與對象" name="audience">
          <input
            id="audience"
            name="audience"
            className={inputClass}
            placeholder="例如：主管、一般員工"
          />
        </FormField>
        <FormField label="希望主題" name="topic" required error={errors.topic}>
          <input id="topic" name="topic" className={inputClass} placeholder="希望演講的主題" />
        </FormField>
        <FormField label="演講時間" name="duration">
          <input
            id="duration"
            name="duration"
            className={inputClass}
            placeholder="例如：60 分鐘"
          />
        </FormField>
        <FormField label="預算" name="budget">
          <input id="budget" name="budget" className={inputClass} placeholder="預算區間（選填）" />
        </FormField>
        <FormField label="其他補充" name="note">
          <textarea
            id="note"
            name="note"
            className={textareaClass}
            maxLength={500}
            placeholder="其他想補充的內容（選填）"
          />
        </FormField>
      </div>

      <input
        type="text"
        name={HONEYPOT_FIELD}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-aux-field"
      />

      {status === "success" && (
        <p className="mt-5 rounded-md bg-success/10 border border-success/30 px-4 py-3 text-sm text-success">
          {SUCCESS_MSG}
        </p>
      )}
      {status === "error" && (
        <p className="mt-5 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
          {errorMsg}
        </p>
      )}

      <div className="mt-6">
        <button type="submit" disabled={status === "submitting"} className={submitButtonClass}>
          {status === "submitting" ? "送出中…" : "送出講師邀約"}
        </button>
      </div>

      <p className="mt-4 text-xs leading-[1.7] text-ink-soft/70">
        送出表單即表示您同意本網站依
        <a href="#" className="text-primary hover:text-gold underline">
          隱私權政策
        </a>
        使用您所提供的聯絡資料，以回覆本次詢問與提供相關服務資訊。
      </p>
    </form>
  );
}